import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Navbar.css';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getFrontendAssetUrl } from '../../lib/frontendAssets';

const apiURL = process.env.REACT_APP_API_URL;
const logo = getFrontendAssetUrl('LOGO_FONS_TRANSPARENT.png');
const contact = getFrontendAssetUrl('Contacte.png');
const iconOnSom = getFrontendAssetUrl('LocationIcon.png');
const iconShCart = getFrontendAssetUrl('shopping-cart-icon-disabled.png');
const iconX = getFrontendAssetUrl('XIcon.png');

const IMPRESORES_SUBCATEGORIES = [
  {
    key: 'impressores',
    label: 'Impressores',
    to: '/categories/impressores',
    sublinks: [
      { label: 'Injecció Tinta', to: '/categories/impressores-injeccio-tinta' },
      { label: 'Làser', to: '/categories/impressores-laser' }
    ]
  },
  {
    key: 'multifuncio',
    label: 'Multifunció',
    to: '/categories/multifuncio',
    sublinks: [
      { label: 'Injecció Tinta', to: '/categories/multifuncio-injeccio-tinta' },
      { label: 'Làser', to: '/categories/multifuncio-laser' }
    ]
  }
];

const CATEGORY_SECTIONS = [
  {
    key: 'dispositius',
    label: 'Dispositius',
    viewAll: '/categories/dispositius',
    columns: [
      {
        links: [
          { label: 'Ordinadors', to: '/categories/ordinadors' },
          { label: 'Portàtils', to: '/categories/portatils' },
          { label: 'All-in-one', to: '/categories/all-in-one' },
          { label: 'Tablets', to: '/categories/tablets' }
        ]
      }
    ]
  },
  {
    key: 'impresores',
    label: 'Impresores',
    viewAll: '/categories/impresores',
    columns: [
      {
        links: IMPRESORES_SUBCATEGORIES
      }
    ]
  },
  {
    key: 'gaming',
    label: 'Gaming',
    href: '/categories/gaming'
  },
  {
    key: 'periferics',
    label: 'Perifèrics',
    viewAll: '/categories/periferics',
    columns: [
      {
        links: [
          { label: 'Teclats', to: '/categories/teclats' },
          { label: 'Ratolins', to: '/categories/ratolins' },
          { label: 'Monitors', to: '/categories/monitors' },
          { label: 'Altaveus', to: '/categories/altaveus' },
          { label: 'Auriculars', to: '/categories/auriculars' },
          { label: 'Disc Durs externs', to: '/categories/disc-durs-externs' }
        ]
      }
    ]
  },
  {
    key: 'components',
    label: 'Components',
    viewAll: '/categories/components',
    columns: [
      {
        links: [
          { label: 'Placa Base', to: '/categories/placa-base' },
          { label: 'Memoria', to: '/categories/memoria' },
          { label: 'Targeta Gràfica', to: '/categories/targeta-grafica' },
          { label: 'Fonts Alimentació', to: '/categories/fonts-alimentacio' },
          { label: 'Discs Durs', to: '/categories/discs-durs' },
          { label: 'Pendrives', to: '/categories/pendrives' }
        ]
      }
    ]
  },
  {
    key: 'xarxa',
    label: 'Xarxa',
    viewAll: '/categories/xarxa',
    columns: [
      {
        links: [
          { label: 'Targetes de xarxa', to: '/categories/targetes-de-xarxa' },
          { label: 'Routers', to: '/categories/routers' },
          { label: 'Switchs', to: '/categories/switchs' },
          { label: 'Cables de xarxa', to: '/categories/cables-de-xarxa' }
        ]
      }
    ]
  },
  {
    key: 'serveiTecnic',
    label: 'Servei Tècnic',
    href: '/categories/serveiTecnic'
  },
  {
    key: 'software',
    label: 'Software',
    viewAll: '/categories/software',
    columns: [
      {
        links: [
          { label: 'Antivirus', to: '/categories/antivirus' },
          { label: 'Llicències Windows', to: '/categories/licencies-windows' },
          { label: 'Pack Office', to: '/categories/pack-office' }
        ]
      }
    ]
  }
];

const normalizeText = (value) => value
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const levenshteinDistance = (source, target) => {
  if (source === target) {
    return 0;
  }

  if (!source) {
    return target.length;
  }

  if (!target) {
    return source.length;
  }

  const previousRow = Array.from({ length: target.length + 1 }, (_, index) => index);

  for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
    let previousDiagonal = previousRow[0];
    previousRow[0] = sourceIndex;

    for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
      const previousValue = previousRow[targetIndex];
      const cost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;

      previousRow[targetIndex] = Math.min(
        previousRow[targetIndex] + 1,
        previousRow[targetIndex - 1] + 1,
        previousDiagonal + cost
      );

      previousDiagonal = previousValue;
    }
  }

  return previousRow[target.length];
};

const getSearchScore = (query, title) => {
  const normalizedQuery = normalizeText(query);
  const normalizedTitle = normalizeText(title);

  if (!normalizedQuery || !normalizedTitle) {
    return 0;
  }

  const queryTokens = normalizedQuery.split(' ');
  const titleTokens = normalizedTitle.split(' ');
  const titleHasQuery = normalizedTitle.includes(normalizedQuery);
  const exactMatch = normalizedTitle === normalizedQuery;

  let score = 0;

  if (exactMatch) {
    score += 1000;
  }

  if (titleHasQuery) {
    score += 500 - normalizedTitle.indexOf(normalizedQuery);
  }

  const queryTokenMatches = queryTokens.filter((queryToken) =>
    queryToken && titleTokens.some((titleToken) => titleToken.includes(queryToken))
  ).length;

  score += queryTokenMatches * 120;

  const distance = levenshteinDistance(normalizedQuery, normalizedTitle);
  const maxLength = Math.max(normalizedQuery.length, normalizedTitle.length);
  const similarity = maxLength === 0 ? 0 : 1 - (distance / maxLength);

  score += similarity * 100;

  if (!titleHasQuery && queryTokenMatches === 0 && similarity < 0.28) {
    return 0;
  }

  return score;
};

const Navbar = () => {
  const categoriesNavRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const [searchProducts, setSearchProducts] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(true);
  const [hoveredCategoryGroup, setHoveredCategoryGroup] = useState(null);
  const [pinnedCategoryGroup, setPinnedCategoryGroup] = useState(null);
  const [hoveredImpresoresSubcategory, setHoveredImpresoresSubcategory] = useState(null);

  useEffect(() => {
    const handleClickOutsideBox = (event) => {
      const clickedInsideCategories = categoriesNavRef.current?.contains(event.target);

      if (!clickedInsideCategories) {
        setHoveredCategoryGroup(null);
        setPinnedCategoryGroup(null);
        setHoveredImpresoresSubcategory(null);
      }

      if (!searchWrapperRef.current?.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideBox);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideBox);
    };
  }, []);

  useEffect(() => {
    const getSearchProducts = async () => {
      try {
        setIsSearchLoading(true);

        if (!apiURL) {
          setSearchProducts([]);
          return;
        }

        const response = await fetch(`${apiURL}/allproducts`);

        if (!response.ok) {
          throw new Error('No s’han pogut carregar els productes');
        }

        const products = await response.json();
        setSearchProducts(Array.isArray(products) ? products : []);
      } catch (error) {
        setSearchProducts([]);
      } finally {
        setIsSearchLoading(false);
      }
    };

    getSearchProducts();
  }, []);

  const searchResults = useMemo(() => {
    const trimmedValue = searchValue.trim();

    if (!trimmedValue) {
      return [];
    }

    return searchProducts
      .map((product) => ({
        ...product,
        searchScore: getSearchScore(trimmedValue, product?.name || '')
      }))
      .filter((product) => product.searchScore > 0)
      .sort((leftProduct, rightProduct) => rightProduct.searchScore - leftProduct.searchScore)
      .slice(0, 5);
  }, [searchProducts, searchValue]);

  const showSearchDropdown = isSearchFocused || searchValue.trim().length > 0;
  const activeCategoryGroup = pinnedCategoryGroup || hoveredCategoryGroup;

  const closeCategoryMenu = () => {
    setHoveredCategoryGroup(null);
    setPinnedCategoryGroup(null);
    setHoveredImpresoresSubcategory(null);
  };

  const toggleCategoryMenu = (groupKey) => {
    setHoveredCategoryGroup(groupKey);
    setPinnedCategoryGroup((currentPinnedKey) => (currentPinnedKey === groupKey ? null : groupKey));
    setHoveredImpresoresSubcategory(null);
  };

  return (
    <>
      {showSearchDropdown && (
        <button
          type="button"
          className="search-focus-overlay"
          onClick={() => setIsSearchFocused(false)}
          aria-label="Tanca la cerca"
        />
      )}

      <div className={`navbar ${showSearchDropdown ? 'navbar--search-active' : ''}`}>
        <div className="navbar-top">
          <div className="navbar-logo">
            <Link to='/'>
              <img src={logo} alt="Equips Oficina Anoia" />
            </Link>
            <div className="navbar-logo-text">
              <p>Equips Oficina</p>
              <p className='navbar-logo-text-p-Anoia'>Anoia</p>
            </div>
          </div>

          <div className={`navbar-search-wrapper ${showSearchDropdown ? 'navbar-search-wrapper--active' : ''}`} ref={searchWrapperRef}>
            <form className="navbar-search" onSubmit={(event) => event.preventDefault()}>
          <input
            className="navbar-search-bar"
            type="text"
            value={searchValue}
            placeholder="Què estàs buscant?"
            id='In'
            onFocus={() => setIsSearchFocused(true)}
            onChange={(event) => setSearchValue(event.target.value)}
          />

          <AnimatePresence>
            {searchValue !== '' && (
              <motion.button
                type="button"
                className='inputSearch'
                initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0, rotate: 45 }}
                transition={{ duration: 0.3 }}
                onClick={() => {
                  setSearchValue('');
                  setIsSearchFocused(true);
                }}
                aria-label="Neteja la cerca"
              >
                <img src={iconX} alt="" style={{ width: '16px', height: '16px' }} />
              </motion.button>
            )}
          </AnimatePresence>

          <button className="buttonSearch" type="submit">
            <i className="fa fa-search" aria-hidden="true"></i>
          </button>
            </form>

            <AnimatePresence>
              {showSearchDropdown && (
                <motion.div
                  className="search-results-panel"
                  initial={{ opacity: 0, y: -8, scaleY: 0.96 }}
                  animate={{ opacity: 1, y: 0, scaleY: 1 }}
                  exit={{ opacity: 0, y: -8, scaleY: 0.96 }}
                  transition={{ duration: 0.18 }}
                >
                  {searchValue.trim().length === 0 ? (
                    <p className="search-results-placeholder">Comença a escriure per veure productes relacionats.</p>
                  ) : isSearchLoading ? (
                    <p className="search-results-placeholder">Carregant productes...</p>
                  ) : searchResults.length > 0 ? (
                    <div className="search-results-list">
                      {searchResults.map((product) => (
                        <Link
                          key={product.id ?? product.name}
                          to={`/product/${encodeURIComponent(product.name)}`}
                          className="search-result-item"
                          onClick={() => {
                            setSearchValue('');
                            setIsSearchFocused(false);
                          }}
                        >
                          <div className="search-result-image-wrap">
                            {product.image ? (
                              <img
                                src={`${apiURL}/images/${product.image}`}
                                alt={product.name}
                                className="search-result-image"
                              />
                            ) : (
                              <div className="search-result-image search-result-image--empty" />
                            )}
                          </div>
                          <div className="search-result-content">
                            <span className="search-result-title">{product.name}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="search-results-empty">No hem trobat cap producte semblant. Prova amb una altra paraula.</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="infoButtons">
            <button className='buttonOnSom' onClick={() => document.getElementById('OnSom').scrollIntoView()}>
              <img src={iconOnSom} alt="" />
            </button>

            <button className='buttonContact' onClick={() => document.getElementById('Contacte').scrollIntoView()}>
              <img src={contact} alt="" />
            </button>

            <button className='buttonShoppingCart'>
              <img src={iconShCart} alt="" />
            </button>
          </div>
        </div>

        <div
          className="navbar-categories-section"
          ref={categoriesNavRef}
        >
          <div className="navbar-categories-row">
            {CATEGORY_SECTIONS.map((section) => {
              const hasSubcategories = Array.isArray(section.columns) && section.columns.length > 0;
              const isActiveSection = activeCategoryGroup === section.key;

              return (
                <div
                  key={section.key}
                  className={`navbar-category-item ${isActiveSection ? 'navbar-category-item--active' : ''}`}
                  onMouseEnter={() => hasSubcategories && setHoveredCategoryGroup(section.key)}
                  onMouseLeave={() => {
                    if (!pinnedCategoryGroup) {
                      setHoveredCategoryGroup(null);
                    }

                    if (section.key === 'impresores') {
                      setHoveredImpresoresSubcategory(null);
                    }
                  }}
                  onFocus={() => hasSubcategories && setHoveredCategoryGroup(section.key)}
                >
                  {section.href ? (
                    <Link to={section.href} className="navbar-category-link navbar-category-link--direct" onClick={closeCategoryMenu}>
                      {section.label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="navbar-category-link navbar-category-link--toggle"
                      onClick={() => toggleCategoryMenu(section.key)}
                      aria-expanded={isActiveSection}
                    >
                      <span>{section.label}</span>
                    </button>
                  )}

                  <AnimatePresence>
                    {hasSubcategories && isActiveSection && (
                      <motion.div
                        className="navbar-category-submenu"
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.16 }}
                      >
                        <div className="navbar-category-submenu-grid">
                          {section.columns.map((column, columnIndex) => (
                            <div className="navbar-category-submenu-card" key={`${section.key}-${columnIndex}`}>
                              {column.title && <p className="navbar-category-submenu-title">{column.title}</p>}
                              <div className="navbar-category-submenu-links">
                                {column.links.map((link) => {
                                  const hasNestedLinks = section.key === 'impresores' && Array.isArray(link.sublinks) && link.sublinks.length > 0;
                                  const isNestedOpen = hoveredImpresoresSubcategory === link.key;

                                  return (
                                    <div
                                      key={link.key || link.to}
                                      className={`navbar-category-submenu-item ${hasNestedLinks ? 'navbar-category-submenu-item--has-nested' : ''} ${isNestedOpen ? 'navbar-category-submenu-item--active' : ''}`}
                                      onMouseEnter={() => hasNestedLinks && setHoveredImpresoresSubcategory(link.key)}
                                      onMouseLeave={() => {
                                        if (hoveredImpresoresSubcategory === link.key) {
                                          setHoveredImpresoresSubcategory(null);
                                        }
                                      }}
                                    >
                                      <Link
                                        to={link.to}
                                        className="navbar-category-submenu-link"
                                        onClick={closeCategoryMenu}
                                      >
                                        {link.label}
                                      </Link>

                                      <AnimatePresence>
                                        {hasNestedLinks && isNestedOpen && (
                                          <motion.div
                                            className="navbar-category-subsubmenu"
                                            initial={{ opacity: 0, y: -6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -6 }}
                                            transition={{ duration: 0.16 }}
                                          >
                                            <div className="navbar-category-subsubmenu-links">
                                              {link.sublinks.map((sublink) => (
                                                <Link
                                                  key={sublink.to}
                                                  to={sublink.to}
                                                  className="navbar-category-subsubmenu-link"
                                                  onClick={closeCategoryMenu}
                                                >
                                                  {sublink.label}
                                                </Link>
                                              ))}
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
